import {CommonModule} from '@angular/common';
import {Component, computed, effect, resource, Resource, signal, Signal, TemplateRef, viewChild} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatTableModule} from '@angular/material/table';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {FileTableComponent} from '@app/files/file-table/file-table.component';
import {PermissionsTableComponent} from '@app/permissions/permissions-table/permissions-table.component';
import {mapUsersToPermissionRows, PermissionsContext} from '@app/permissions/permissions.mapper';
import {pdcRoutes, pdeRoutes} from '@app/routing/routes';
import {
  DatasetResponse,
  DatasetsService,
  FileVersionResponse,
  ProjectsService,
  RedcapDatasetResponse,
  RelatedRecordResponse,
} from '@backend';
import {CommonsSearchDataset} from '@models/commons-types';
import {AttestationService} from '@services/attestation/attestation.service';
import {LandingServiceConfigService} from '@services/config/landing-service-config.service';
import {LandingServiceBrokerService} from '@services/landing-service-broker/landing-service-broker.service';
import {
  KeyValueTableComponent,
  keyValueTableObject,
} from '@shared/components/key-value-table/key-value-table.component';
import {SectionHeaderActions} from '@shared/components/section-header-actions/section-header-actions';
import {DatasetRoleOptions} from '@shared/labels/dataset-role';
import {SensitivityLevel} from '@shared/labels/sensitivity-level';
import {formatSize} from '@utilities/format-size';
import {lastValueFrom, map} from 'rxjs';

const HSD_DOWNLOAD_ATTESTATION_TITLE = 'Download highly-sensitive dataset';
const NON_HSD_DOWNLOAD_ATTESTATION_TITLE = 'Download dataset';

@Component({
  selector: 'app-dataset-view-private',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatTableModule,
    KeyValueTableComponent,
    RouterLink,
    FileTableComponent,
    PermissionsTableComponent,
  ],
  templateUrl: './dataset-view-private.html',
})
export class DatasetViewPrivate implements SectionHeaderActions {
  public dataset: Signal<DatasetResponse & CommonsSearchDataset>;
  public projectStub: Signal<RelatedRecordResponse> = computed(
    () => this.dataset().associated_projects.find(p => p.is_primary_project).project,
  );
  private readonly datasetsService: Resource<DatasetsService>;
  private readonly projectsService: Resource<ProjectsService>;
  private readonly documentLinksResource: Resource<{contract: string; dsp: string}>;
  hsd_download_attestation: Signal<TemplateRef<undefined>> = viewChild.required('hsd_download_attestation');
  non_hsd_download_attestation: Signal<TemplateRef<undefined>> = viewChild.required('non_hsd_download_attestation');
  can_show_file_publicity_status = signal(true);
  unaffected_by_hsd_download_restrictions = signal(true);

  get datasetPermissionContext(): PermissionsContext {
    return {
      type: 'dataset' as const,
      id: this.dataset()?.id,
      editable: false,
      showEditButton: false,
      orgName: this.dataset()?.storage_organization?.full_name,
      organizationalAccess: computed(() => this.dataset()?.organizational_access),
    };
  }
  datasetPermissions = computed(() => {
    if (!this.dataset()) return [];
    return mapUsersToPermissionRows(this.dataset().dataset_users, this.datasetPermissionContext, DatasetRoleOptions);
  });

  constructor(
    activatedRoute: ActivatedRoute,
    private readonly sanitizer: DomSanitizer,
    lsbs: LandingServiceBrokerService,
    lscs: LandingServiceConfigService,
    private readonly attestationService: AttestationService,
  ) {
    this.dataset = toSignal(activatedRoute.data.pipe(map(data => data['dataset'])));
    this.datasetsService = resource({
      params: () => ({storage_org_name: this.dataset()?.storage_organization?.name}),
      async loader({params}) {
        return (await lsbs.getByOrganizationName(params.storage_org_name)).DatasetsService;
      },
    });
    this.projectsService = resource({
      params: () => ({storage_org_name: this.dataset()?.storage_organization?.name}),
      async loader({params}) {
        return (await lsbs.getByOrganizationName(params.storage_org_name)).ProjectsService;
      },
    });

    this.documentLinksResource = resource({
      params: () => {
        const dataset = this.dataset();
        if (!dataset || !this.projectsService.hasValue()) return undefined;
        const primaryProject = dataset.associated_projects.find(project => project.is_primary_project);
        const contractId = primaryProject.contract?.id;
        const dspId = primaryProject.data_security_plan?.id;
        return {projectId: primaryProject.project.id, contractId, dspId, projectsService: this.projectsService.value()};
      },
      async loader({params}) {
        let contract, dsp;
        if (params.contractId)
          contract = (
            await lastValueFrom(
              params.projectsService.generateProjectFileDownloadLink(params.projectId, params.contractId),
            )
          ).download_url;
        if (params.dspId)
          dsp = (
            await lastValueFrom(params.projectsService.generateProjectFileDownloadLink(params.projectId, params.dspId))
          ).download_url;
        return {contract, dsp};
      },
    });

    const config = resource({
      params: () => this.dataset()?.storage_organization?.name,
      loader: async ({params}) => lscs.getPromise(params),
    });

    effect(() => {
      if (config.hasValue()) {
        this.can_show_file_publicity_status.set(
          config.value().allow_dataset_data_public &&
            (this.dataset().dataset_file_versions.length > 0 || config.value().allow_file_uploads),
        );
        this.unaffected_by_hsd_download_restrictions.set(
          this.dataset().sensitivity_level.id != SensitivityLevel.HSD || config.value().allow_hsd_data_download,
        );
      }
    });
  }

  public sectionHeaderActions = signal([
    {
      target: '/help/private-data-enclave/view-private-dataset',
      targetTab: '_blank',
      caption: 'Help',
      color: 'var(--mat-sys-primary)',
      icon: 'help_outline',
    },
  ]);

  public readonly safeDescription = computed<SafeHtml>(() => {
    const description = this.dataset()?.description;
    if (!description) return '';

    // Bypass Angular's sanitizer to allow this specific piece of HTML to be WYSIWYG
    return this.sanitizer.bypassSecurityTrustHtml(description); // nosonar
  });

  public readonly latestFileVersion = computed(() =>
    this.dataset()?.dataset_file_versions?.find(dsfv => dsfv.is_latest_and_not_deleted),
  );

  public formatFileSize(bytes: number): string {
    return formatSize(bytes);
  }

  public readonly buildKeyValueTable = computed<keyValueTableObject>(() => {
    const dataset = this.dataset();
    if (!dataset) return [];
    const tableData: keyValueTableObject = [];

    const primary_project = dataset.associated_projects.find(project => project.is_primary_project);
    const other_projects = dataset.associated_projects.filter(project => !project.is_primary_project);
    other_projects.sort((a, b) => a.project.name.localeCompare(b.project.name));

    tableData.push(
      {key: 'dataset_type', display_name: 'Dataset Type', value: dataset.dataset_type},
      {
        key: 'primary_project',
        display_name: 'Primary Associated Project',
        value: primary_project.project.name,
        link: '/' + pdeRoutes.projectView(primary_project.project.id).slice(1).join('/'),
      },
      {
        key: 'other_projects',
        display_name: 'Other Associated Projects',
        value: other_projects.map(project => project.project.name),
      },
      {key: 'keywords', display_name: 'Keywords', value: dataset.keywords.join(', ')},
      {
        key: 'storage_organization',
        display_name: 'Storage Organization',
        value: dataset.storage_organization.full_name,
      },
      {
        key: 'identifiers_hipaa',
        display_name: 'HIPAA Identifiers',
        value: dataset.identifiers_hipaa.map(hipaa => hipaa.text),
      },
      {key: 'study_irb_number', display_name: 'IRB Study Number', value: dataset.study_irb_number},
      {
        key: 'data_security_plan',
        display_name: 'Data Security Plan',
        value: dataset.associated_projects.find(project => project.is_primary_project).data_security_plan?.file_name,
        link: this.documentLinksResource.hasValue() ? this.documentLinksResource.value().dsp : undefined,
      },
      {key: 'data_aggregation', display_name: 'Data Granularity', value: dataset.data_aggregation.text},
      {
        key: 'other_sensitive_data',
        display_name: 'Other Sensitive Data',
        value: dataset.other_sensitive_data.map(osd => osd.text),
      },
      {
        key: 'contract',
        display_name: 'Contract',
        value: dataset.associated_projects.find(project => project.is_primary_project).contract?.file_name,
        link: this.documentLinksResource.hasValue() ? this.documentLinksResource.value().contract : undefined,
      },
      {key: 'license', display_name: 'License', value: dataset.license?.text, link: dataset.license?.url},
      {
        key: 'doi',
        display_name: 'Document Object Identifier (DOI)',
        value: dataset.doi,
        link: dataset.doi?.startsWith('doi:') ? 'https://doi.org/' + dataset.doi.substring(4) : undefined,
      },
      {key: 'attribution', display_name: 'Attribution', value: dataset.attribution},
      {
        key: 'link_to_external_dataset',
        display_name: 'Link to External Dataset File',
        value: dataset.link_to_external_dataset,
        link: dataset.link_to_external_dataset,
      },
    );
    if (dataset.dataset_type == 'REDCap') {
      tableData.push(
        {
          key: 'redcap_project_url',
          display_name: 'REDCap Project URL',
          value: dataset.redcap_metadata.redcap_project_url,
          link: dataset.redcap_metadata.redcap_project_url,
        },
        {
          key: 'redcap_extract_data',
          display_name: 'Extract from REDCap',
          value: this.getREDCapExtractPlanName(dataset),
        },
        {
          key: 'redcap_report_id',
          display_name: 'REDCap Report ID',
          value: dataset.redcap_metadata.redcap_report_id,
        },
        {
          key: 'redcap_project_title',
          display_name: 'REDCap Project Title',
          value: dataset.redcap_metadata.redcap_project_title,
        },
        {
          key: 'redcap_project_pi',
          display_name: 'REDCap Project PI',
          value: dataset.redcap_metadata.redcap_project_pi,
        },
        // TODO: not available until PR #1144 lands
        // {
        //   key:'redcap_last_data_edit_timestamp',
        //   display_name:'Last Extracted from REDCap',
        //   value:dataset.redcap_metadata.last_redcap_data_edit_timestamp
        // },
        {
          key: 'redcap_extract_status',
          display_name: 'REDCap Extract Status',
          value: this.getREDCapExtractStatusText(dataset.redcap_metadata.extract_status),
        },
      );
    }
    if (dataset.dataset_type == 'DICOM') {
      tableData.push(
        {
          key: 'bids_structure',
          display_name: 'Brain Imaging Data Structure (BIDS)',
          value: dataset.dicom_metadata.bids_structure ? 'Yes' : 'No',
        },
        {
          key: 'quality',
          display_name: 'Quality',
          value: dataset.dicom_metadata.quality[0].toUpperCase() + dataset.dicom_metadata.quality.substring(1),
        },
      );
      dataset.dicom_metadata.de_identified = undefined;
    } else {
      tableData.push({
        key: 'variables_measured',
        display_name: 'Variables Measured',
        value: dataset.variables_measured.join(', '),
      });
    }
    return tableData;
  });

  private getREDCapExtractPlanName(dataset: RedcapDatasetResponse) {
    if (!dataset.redcap_metadata.redcap_extract_data) return 'None';
    if (dataset.redcap_metadata.is_data_dictionary) return 'Data Dictionary';
    if (!dataset.redcap_metadata.redcap_report_id) return 'All Data';
    return 'Single Report';
  }

  public readonly buildDicomTable = computed<keyValueTableObject>(() => {
    const dataset = this.dataset();
    if (dataset?.dataset_type != 'DICOM') return [];
    const labels = {
      acquisition_date: 'Acquisition Date',
      angio_flag: 'Angio Flag',
      bids_structure: 'Brain Imaging Data Structure (BIDS)',
      de_identified: 'De-identified',
      field_of_view: 'Field of View',
      field_strength: 'Field Strength',
      institution_name: 'Imaging Institution Name',
      largest_image_pixel_value: 'Largest Image Pixel Value',
      modality: 'Modality',
      mr_acquisition_type: 'MR Acquisition Type',
      organ_name: 'Organ Name',
      patient_sex: 'Patient Gender',
      protocol_name: 'Protocol Name',
      quality: 'Quality',
      scanner_manufacturer_name: 'Scanner Manufacturer',
      scanner_model_name: 'Scanner Model',
      software_versions: 'Software Versions',
      study_date: 'Study Date',
    };
    return Object.keys(dataset.dicom_metadata)
      .filter(k => k != 'bids_structure' && k != 'quality')
      .map(key => ({
        key,
        display_name: labels[key] ?? key,
        value:
          typeof dataset.dicom_metadata[key] == 'boolean'
            ? dataset.dicom_metadata[key]
              ? 'Yes'
              : 'No'
            : dataset.dicom_metadata[key],
      }));
  });

  public get_file_size(file: FileVersionResponse) {
    return formatSize(file.file_size);
  }

  public async downloadLatestVersion(): Promise<void> {
    let isHSD = this.dataset().sensitivity_level.id === SensitivityLevel.HSD;
    if (
      !(await this.attestationService.confirmWithCancel(
        isHSD ? this.hsd_download_attestation() : this.non_hsd_download_attestation(),
        isHSD ? HSD_DOWNLOAD_ATTESTATION_TITLE : NON_HSD_DOWNLOAD_ATTESTATION_TITLE,
      ))
    )
      return;
    const fv = this.latestFileVersion();
    if (!fv || !this.datasetsService.hasValue()) return;

    const result = await lastValueFrom(
      this.datasetsService.value().generateDatasetFileDownloadLink(this.dataset().id, fv.file_version.id),
    );

    const url = result.download_url;
    const a = document.createElement('a');
    a.href = url;
    a.download = fv.file_version.file_name;
    a.click();
  }

  public getREDCapExtractStatusText(raw_text: string): string {
    const json_match = /\{"error":.*\}/.exec(raw_text);
    const html_match = /^<!DOCTYPE.*<h2>([^<]+)<\/h2>\s*<h3>([^<]+)<\/h3>/.exec(raw_text);
    if (html_match !== null) {
      return raw_text.substring(0, html_match.index) + html_match[1] + ' \u2014 ' + html_match[2];
    }
    if (json_match !== null) {
      return raw_text.substring(0, json_match.index) + JSON.parse(json_match[0])['error'];
    }
    return raw_text;
  }

  async openDSP() {
    const project = this.dataset().associated_projects.find(p => p.is_primary_project);
    const dsp = project.data_security_plan.id;
    const link = await lastValueFrom(
      this.projectsService.value().generateProjectFileDownloadLink(project.project.id, dsp),
    );
    globalThis.open(link.download_url, 'RDE_DSP');
  }

  protected readonly pdcRoutes = pdcRoutes;
  protected readonly pdeRoutes = pdeRoutes;
}
